import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, CreditCard, Plus, Search, Edit2, Trash2, X, Clock, Check, Layers } from 'lucide-react';
import './CompanyManagement.css';
import './SubscriptionPlanManagement.css';

const getPlanItems = (plan) => {
    if (Array.isArray(plan?.item_ids) && plan.item_ids.length > 0) {
        return plan.item_ids.map(item => (typeof item === 'object' && item !== null) ? item : { _id: item, item_code: 'ITEM', item_name: 'Item' });
    }
    if (plan?.item_id) {
        const item = plan.item_id;
        return [(typeof item === 'object' && item !== null) ? item : { _id: item, item_code: 'ITEM', item_name: 'Item' }];
    }
    return [];
};

const SubscriptionPlanManagement = () => {
    const navigate = useNavigate();
    const [plans, setPlans] = useState([]);
    const [subscriptionItems, setSubscriptionItems] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [itemSearchTerm, setItemSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPlan, setEditingPlan] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [companies, setCompanies] = useState([]);
    const [newOverrideCompanyId, setNewOverrideCompanyId] = useState('');
    const [newOverrideRate, setNewOverrideRate] = useState('');

    const [formData, setFormData] = useState({
        plan_name: '',
        item_ids: [],
        currency: 'INR',
        pricing_method: 'FIXED',
        rate: '',
        billing_interval: 'MONTH',
        billing_interval_count: 1,
        follow_calendar_months: false,
        status: 'ACTIVE',
        grace_period_days: 7,
        cancel_invoice_after_grace_period: false,
        company_overrides: []
    });

    const serverUrl = process.env.REACT_APP_SERVER_BASE_URL || 'http://localhost:8080';

    useEffect(() => {
        const userRole = (localStorage.getItem('userRole') || '').toLowerCase();
        if (userRole !== 'superadmin') {
            navigate('/admin-dashboard', { replace: true });
            return;
        }
        fetchPlansAndItems();
    }, [navigate]);

    const fetchPlansAndItems = async () => {
        setIsLoading(true);
        try {
            const [itemsRes, plansRes, compRes] = await Promise.all([
                fetch(`${serverUrl}/api/subscription-items`),
                fetch(`${serverUrl}/api/subscription-plans`),
                fetch(`${serverUrl}/api/companies`)
            ]);

            if (compRes.ok) {
                const compData = await compRes.json();
                setCompanies(compData);
            }

            if (itemsRes.ok) {
                const fetchedItems = await itemsRes.json();
                setSubscriptionItems(fetchedItems);
            }

            if (plansRes.ok) {
                const plansData = await plansRes.json();
                setPlans(plansData);
            }
        } catch (err) {
            console.log('Server unreachable, using local fallback subscription data');
            const fallbackComps = [
                { _id: 'comp_1', name: 'Federal Steel Industries', code: 'FSI-001' },
                { _id: 'comp_2', name: 'Apex Alloys Manufacturing', code: 'AAM-002' }
            ];
            setCompanies(fallbackComps);
            const fallbackItems = [
                { _id: 'item_cu', item_code: 'COPPER', item_name: 'Copper Metallurgy', description: 'Copper Metallurgy Testing & Analytics', status: 'ACTIVE' },
                { _id: 'item_fe', item_code: 'IRON', item_name: 'Iron & Steel Metallurgy', description: 'Iron & Steel Metallurgy Tools', status: 'ACTIVE' },
                { _id: 'item_tensile', item_code: 'TENSILE', item_name: 'Tensile Testing', description: 'Tensile & Mechanical Strength Module', status: 'ACTIVE' },
                { _id: 'item_charpy', item_code: 'CHARPY', item_name: 'Charpy Impact Test', description: 'Impact Energy & Fracture Analysis', status: 'ACTIVE' }
            ];
            setSubscriptionItems(fallbackItems);
            setPlans([
                {
                    _id: 'plan_1',
                    plan_name: 'Copper & Tensile Standard Monthly',
                    item_ids: [fallbackItems[0], fallbackItems[2]],
                    item_id: fallbackItems[0],
                    currency: 'INR',
                    pricing_method: 'FIXED',
                    rate: 5000,
                    billing_interval: 'MONTH',
                    billing_interval_count: 1,
                    follow_calendar_months: false,
                    status: 'ACTIVE',
                    grace_period_days: 7,
                    cancel_invoice_after_grace_period: false,
                    company_overrides: [],
                    created_at: new Date().toISOString()
                }
            ]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = (plan = null) => {
        setError('');
        setItemSearchTerm('');
        setNewOverrideCompanyId('');
        setNewOverrideRate('');
        if (plan) {
            setEditingPlan(plan);
            const planItems = getPlanItems(plan);
            const initialItemIds = planItems.map(item => item._id || item);
            const formattedOverrides = Array.isArray(plan.company_overrides)
                ? plan.company_overrides.map(o => ({
                    company_id: o.company_id?._id || o.company_id,
                    negotiated_rate: o.negotiated_rate
                }))
                : [];
            setFormData({
                plan_name: plan.plan_name || '',
                item_ids: initialItemIds,
                currency: plan.currency || 'INR',
                pricing_method: plan.pricing_method || 'FIXED',
                rate: plan.rate ?? '',
                billing_interval: plan.billing_interval || 'MONTH',
                billing_interval_count: plan.billing_interval_count || 1,
                follow_calendar_months: Boolean(plan.follow_calendar_months),
                status: plan.status || 'ACTIVE',
                grace_period_days: plan.grace_period_days ?? 7,
                cancel_invoice_after_grace_period: Boolean(plan.cancel_invoice_after_grace_period),
                company_overrides: formattedOverrides
            });
        } else {
            setEditingPlan(null);
            setFormData({
                plan_name: '',
                item_ids: subscriptionItems.length > 0 ? [subscriptionItems[0]._id] : [],
                currency: 'INR',
                pricing_method: 'FIXED',
                rate: '',
                billing_interval: 'MONTH',
                billing_interval_count: 1,
                follow_calendar_months: false,
                status: 'ACTIVE',
                grace_period_days: 7,
                cancel_invoice_after_grace_period: false,
                company_overrides: []
            });
        }
        setIsModalOpen(true);
    };

    const handleAddCompanyOverride = () => {
        if (!newOverrideCompanyId || newOverrideRate === '' || isNaN(newOverrideRate)) return;
        const compIdStr = newOverrideCompanyId.toString();
        // Constraint: One company can only be tagged once per negotiation plan
        const exists = formData.company_overrides.some(o => (o.company_id?._id || o.company_id).toString() === compIdStr);
        if (exists) return;

        setFormData(prev => ({
            ...prev,
            company_overrides: [
                ...prev.company_overrides,
                { company_id: newOverrideCompanyId, negotiated_rate: Number(newOverrideRate) }
            ]
        }));
        setNewOverrideCompanyId('');
        setNewOverrideRate('');
    };

    const handleRemoveCompanyOverride = (compId) => {
        setFormData(prev => ({
            ...prev,
            company_overrides: prev.company_overrides.filter(o => (o.company_id?._id || o.company_id).toString() !== compId.toString())
        }));
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingPlan(null);
        setError('');
        setItemSearchTerm('');
    };

    const handleToggleItem = (itemId) => {
        setFormData(prev => {
            const exists = prev.item_ids.includes(itemId);
            if (exists) {
                return { ...prev, item_ids: prev.item_ids.filter(id => id !== itemId) };
            } else {
                return { ...prev, item_ids: [...prev.item_ids, itemId] };
            }
        });
    };

    const handleSelectAllItems = () => {
        setFormData(prev => ({
            ...prev,
            item_ids: subscriptionItems.map(item => item._id)
        }));
    };

    const handleDeselectAllItems = () => {
        setFormData(prev => ({
            ...prev,
            item_ids: []
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!formData.plan_name.trim() || formData.item_ids.length === 0 || formData.rate === '') {
            setError('Plan Name, at least one Linked Feature/Item, and Selling Rate are required.');
            return;
        }

        const payload = {
            plan_name: formData.plan_name.trim(),
            item_ids: formData.item_ids,
            item_id: formData.item_ids[0] || null,
            currency: formData.currency.toUpperCase(),
            pricing_method: 'FIXED',
            rate: Number(formData.rate),
            billing_interval: formData.billing_interval,
            billing_interval_count: 1,
            follow_calendar_months: false,
            status: formData.status,
            grace_period_days: Number(formData.grace_period_days) || 7,
            cancel_invoice_after_grace_period: false,
            company_overrides: formData.company_overrides
        };

        try {
            const url = editingPlan
                ? `${serverUrl}/api/subscription-plans/${editingPlan._id}`
                : `${serverUrl}/api/subscription-plans`;
            const method = editingPlan ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const errData = await res.json();
                setError(errData.message || 'Failed to save subscription plan.');
                return;
            }

            fetchPlansAndItems();
            handleCloseModal();
        } catch (err) {
            // Local state fallback update
            const selectedObjs = subscriptionItems.filter(i => formData.item_ids.includes(i._id));
            if (editingPlan) {
                setPlans(prev => prev.map(p => p._id === editingPlan._id ? { ...p, ...payload, item_ids: selectedObjs, item_id: selectedObjs[0] } : p));
            } else {
                setPlans(prev => [{ _id: `plan_${Date.now()}`, ...payload, item_ids: selectedObjs, item_id: selectedObjs[0], created_at: new Date().toISOString() }, ...prev]);
            }
            handleCloseModal();
        }
    };

    const handleDeletePlan = async (planId) => {
        if (!window.confirm('Are you sure you want to delete this subscription plan?')) return;

        try {
            await fetch(`${serverUrl}/api/subscription-plans/${planId}`, { method: 'DELETE' });
            setPlans(prev => prev.filter(p => p._id !== planId));
        } catch (err) {
            setPlans(prev => prev.filter(p => p._id !== planId));
        }
    };

    const filteredPlans = plans.filter(p => {
        const items = getPlanItems(p);
        const matchesItem = items.some(item =>
            (item.item_code || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (item.item_name || '').toLowerCase().includes(searchTerm.toLowerCase())
        );
        return (
            p.plan_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            matchesItem ||
            p.currency.toLowerCase().includes(searchTerm.toLowerCase())
        );
    });

    return (
        <div className="superadmin-page">
            {/* Header */}
            <div className="page-header">
                <div className="page-header-left">
                    <button onClick={() => navigate('/superadmin-dashboard')} className="btn-secondary back-btn">
                        <ArrowLeft size={16} /> Dashboard
                    </button>
                    <div>
                        <h2 className="text-gradient" style={{ margin: 0, fontSize: '24px' }}>Subscription Plans</h2>
                    </div>
                </div>

                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleOpenModal()}
                    className="btn-primary"
                >
                    <Plus size={18} /> Create Plan
                </motion.button>
            </div>

            {/* Search Filter */}
            <div className="search-filter-bar">
                <div className="search-input-wrapper">
                    <Search className="search-icon-inside" size={18} />
                    <input
                        type="text"
                        className="input-field"
                        style={{ paddingLeft: '42px' }}
                        placeholder="Search plan name, feature/item code, or currency..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Subscription Plans Table */}
            <div className="table-panel glass-panel">
                <table className="custom-table">
                    <thead>
                        <tr>
                            <th>Plan Name</th>
                            <th>Linked Features / Items</th>
                            <th>Selling Rate</th>
                            <th>Billing Interval</th>
                            <th>Grace Period</th>
                            <th>Status</th>
                            <th style={{ textAlign: 'right' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredPlans.length === 0 ? (
                            <tr>
                                <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                                    No subscription plans found. Click "Create Plan" to create a new plan.
                                </td>
                            </tr>
                        ) : (
                            filteredPlans.map(p => {
                                const items = getPlanItems(p);
                                return (
                                    <tr key={p._id}>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 700 }}>
                                                <CreditCard size={18} color="var(--primary-color)" />
                                                {p.plan_name}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="plan-items-cell">
                                                {items.length === 0 ? (
                                                    <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>No items</span>
                                                ) : (
                                                    items.map(item => (
                                                        <span key={item._id || item.item_code || Math.random()} className="plan-badge" title={item.description || item.item_name}>
                                                            <Layers size={11} /> {item.item_name || item.item_code || 'Item'}
                                                        </span>
                                                    ))
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div>
                                                <span className="plan-rate">{p.currency} {Number(p.rate).toLocaleString()}</span>
                                                {Array.isArray(p.company_overrides) && p.company_overrides.length > 0 && (
                                                    <div style={{ fontSize: '11px', color: '#10b981', fontWeight: 600, marginTop: '2px' }}>
                                                        {p.company_overrides.length} Negotiated {p.company_overrides.length === 1 ? 'Override' : 'Overrides'}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <span className="interval-tag">
                                                <Clock size={14} /> Every {p.billing_interval_count} {p.billing_interval?.toLowerCase()}
                                            </span>
                                        </td>
                                        <td>
                                            <span style={{ fontSize: '13px', fontWeight: 600 }}>{p.grace_period_days} Days</span>
                                        </td>
                                        <td>
                                            <span className={`status-tag ${p.status?.toLowerCase()}`}>
                                                {p.status}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            <div className="action-btns" style={{ justifyContent: 'flex-end' }}>
                                                <button onClick={() => handleOpenModal(p)} className="icon-btn" title="Edit Plan">
                                                    <Edit2 size={16} />
                                                </button>
                                                <button onClick={() => handleDeletePlan(p._id)} className="icon-btn delete" title="Delete Plan">
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Create / Edit Plan Modal */}
            <AnimatePresence>
                {isModalOpen && (
                    <div className="modal-overlay">
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="modal-card glass-panel"
                            style={{ maxWidth: '640px' }}
                        >
                            <div className="modal-header">
                                <h3 className="text-gradient" style={{ margin: 0 }}>
                                    {editingPlan ? 'Edit Subscription Plan' : 'Create Subscription Plan'}
                                </h3>
                                <button onClick={handleCloseModal} className="icon-btn">
                                    <X size={20} />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="form-grid">
                                <div className="form-full">
                                    <label className="field-label">Plan Name *</label>
                                    <input
                                        type="text"
                                        className="input-field"
                                        placeholder="e.g. Copper Standard & Mechanical Suite"
                                        value={formData.plan_name}
                                        onChange={(e) => setFormData({ ...formData, plan_name: e.target.value })}
                                        required
                                    />
                                </div>

                                {/* Multi-Select Subscription Items */}
                                <div className="form-full">
                                    <label className="field-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span>Linked Features / Items * ({formData.item_ids.length} selected)</span>
                                        <span style={{ fontSize: '11px', fontWeight: 400, color: 'var(--text-secondary)' }}>Select multiple features to offer in this plan</span>
                                    </label>

                                    <div className="multi-select-box">
                                        {/* Selected chips row */}
                                        <div className="selected-chips-row">
                                            {formData.item_ids.length === 0 ? (
                                                <span style={{ color: 'var(--text-secondary)', fontSize: '13px', fontStyle: 'italic' }}>
                                                    No features selected. Click items below to select them.
                                                </span>
                                            ) : (
                                                formData.item_ids.map(id => {
                                                    const matchedItem = subscriptionItems.find(i => i._id === id);
                                                    const name = matchedItem ? matchedItem.item_name : id;
                                                    return (
                                                        <span key={id} className="selected-chip">
                                                            {name}
                                                            <button type="button" onClick={() => handleToggleItem(id)} title="Remove feature">
                                                                <X size={12} />
                                                            </button>
                                                        </span>
                                                    );
                                                })
                                            )}
                                        </div>

                                        {/* Actions bar & search filter */}
                                        <div className="multi-select-actions">
                                            <input
                                                type="text"
                                                className="multi-select-search"
                                                placeholder="Search items by name or code..."
                                                value={itemSearchTerm}
                                                onChange={(e) => setItemSearchTerm(e.target.value)}
                                            />
                                            <button type="button" onClick={handleSelectAllItems} className="btn-text-sm">
                                                Select All
                                            </button>
                                            <button type="button" onClick={handleDeselectAllItems} className="btn-text-sm" style={{ color: '#ef4444' }}>
                                                Clear
                                            </button>
                                        </div>

                                        {/* Options list */}
                                        <div className="multi-select-options-list">
                                            {subscriptionItems
                                                .filter(item =>
                                                    item.item_name?.toLowerCase().includes(itemSearchTerm.toLowerCase()) ||
                                                    item.item_code?.toLowerCase().includes(itemSearchTerm.toLowerCase()) ||
                                                    item.description?.toLowerCase().includes(itemSearchTerm.toLowerCase())
                                                )
                                                .map(item => {
                                                    const isSelected = formData.item_ids.includes(item._id);
                                                    return (
                                                        <div
                                                            key={item._id}
                                                            className={`multi-select-option-item ${isSelected ? 'selected' : ''}`}
                                                            onClick={() => handleToggleItem(item._id)}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => { }} // handled by parent container onClick
                                                                style={{ accentColor: 'var(--primary-color)', width: '16px', height: '16px', cursor: 'pointer' }}
                                                            />
                                                            <span className="item-code-badge">[{item.item_code}]</span>
                                                            <div style={{ flex: 1 }}>
                                                                <div style={{ fontWeight: isSelected ? 700 : 500 }}>{item.item_name}</div>
                                                                {item.description && (
                                                                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{item.description}</div>
                                                                )}
                                                            </div>
                                                            {isSelected && <Check size={16} color="var(--primary-color)" />}
                                                        </div>
                                                    );
                                                })}
                                            {subscriptionItems.length === 0 && (
                                                <div style={{ padding: '10px', textAlign: 'center', fontSize: '13px', color: 'var(--text-secondary)' }}>
                                                    No subscription items found. Please create subscription items first.
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label className="field-label">Currency *</label>
                                    <select
                                        className="input-field"
                                        value={formData.currency}
                                        onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                                    >
                                        <option value="INR">INR (₹)</option>
                                        <option value="USD">USD ($)</option>
                                        <option value="EUR">EUR (€)</option>
                                        <option value="GBP">GBP (£)</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="field-label">Selling Rate *</label>
                                    <input
                                        type="number"
                                        className="input-field"
                                        placeholder="e.g. 5000"
                                        min="0"
                                        step="0.01"
                                        value={formData.rate}
                                        onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="field-label">Billing Interval *</label>
                                    <select
                                        className="input-field"
                                        value={formData.billing_interval}
                                        onChange={(e) => setFormData({ ...formData, billing_interval: e.target.value })}
                                    >
                                        <option value="MONTH">MONTH</option>
                                        <option value="YEAR">YEAR</option>
                                        <option value="WEEK">WEEK</option>
                                        <option value="DAY">DAY</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="field-label">Grace Period (Days) *</label>
                                    <input
                                        type="number"
                                        className="input-field"
                                        min="0"
                                        value={formData.grace_period_days}
                                        onChange={(e) => setFormData({ ...formData, grace_period_days: e.target.value })}
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="field-label">Status *</label>
                                    <select
                                        className="input-field"
                                        value={formData.status}
                                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                    >
                                        <option value="ACTIVE">ACTIVE</option>
                                        <option value="INACTIVE">INACTIVE</option>
                                    </select>
                                </div>

                                {/* Negotiated Price Overrides (Per-Company) */}
                                <div className="form-full" style={{ marginTop: '12px', borderTop: '1px solid var(--panel-border)', paddingTop: '16px' }}>
                                    <label className="field-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ fontWeight: 700, color: 'var(--primary-color)' }}>Negotiated Price Overrides</span>
                                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Tag company for negotiated custom pricing</span>
                                    </label>

                                    {/* Add Override Control */}
                                    <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', alignItems: 'center' }}>
                                        <select
                                            className="input-field"
                                            style={{ flex: 1.5 }}
                                            value={newOverrideCompanyId}
                                            onChange={(e) => setNewOverrideCompanyId(e.target.value)}
                                        >
                                            <option value="">-- Select Tagged Company --</option>
                                            {companies
                                                .filter(c => !formData.company_overrides.some(o => (o.company_id?._id || o.company_id).toString() === c._id.toString()))
                                                .map(c => (
                                                    <option key={c._id} value={c._id}>
                                                        {c.name} ({c.code || 'ORG'})
                                                    </option>
                                                ))
                                            }
                                        </select>
                                        <input
                                            type="number"
                                            className="input-field"
                                            style={{ flex: 1 }}
                                            placeholder="Rate (INR)"
                                            min="0"
                                            value={newOverrideRate}
                                            onChange={(e) => setNewOverrideRate(e.target.value)}
                                        />
                                        <button
                                            type="button"
                                            onClick={handleAddCompanyOverride}
                                            className="btn-primary"
                                            style={{ padding: '8px 12px', fontSize: '13px', whiteSpace: 'nowrap' }}
                                        >
                                            <Plus size={14} /> Tag Override
                                        </button>
                                    </div>

                                    {/* Tagged Overrides List */}
                                    {formData.company_overrides.length > 0 && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', background: 'rgba(255, 255, 255, 0.03)', padding: '10px', borderRadius: '8px', border: '1px solid var(--panel-border)' }}>
                                            {formData.company_overrides.map(ov => {
                                                const compIdStr = (ov.company_id?._id || ov.company_id).toString();
                                                const matchedComp = companies.find(c => c._id.toString() === compIdStr);
                                                const compName = matchedComp ? matchedComp.name : (ov.company_id?.name || 'Company');
                                                const compCode = matchedComp ? matchedComp.code : (ov.company_id?.code || '');
                                                return (
                                                    <div key={compIdStr} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(99, 102, 241, 0.08)', padding: '6px 12px', borderRadius: '6px', fontSize: '13px' }}>
                                                        <div>
                                                            <strong style={{ color: 'var(--text-primary)' }}>{compName}</strong> {compCode && <span className="company-code-badge" style={{ fontSize: '10px' }}>{compCode}</span>}
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                            <span style={{ fontWeight: 700, color: '#10b981', fontFamily: 'monospace' }}>
                                                                {formData.currency || 'INR'} {Number(ov.negotiated_rate).toLocaleString()}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveCompanyOverride(compIdStr)}
                                                                className="icon-btn delete"
                                                                style={{ padding: '2px' }}
                                                                title="Remove Override"
                                                            >
                                                                <X size={14} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {error && (
                                    <div className="form-full" style={{ color: 'var(--error-color)', fontSize: '13px', background: '#fee2e2', padding: '8px 12px', borderRadius: '6px' }}>
                                        {error}
                                    </div>
                                )}

                                <div className="form-full" style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
                                    <button type="button" onClick={handleCloseModal} className="btn-secondary">
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn-primary">
                                        {editingPlan ? 'Save Changes' : 'Create Subscription Plan'}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default SubscriptionPlanManagement;
